import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateSkill, useEmployeeSkills, useRemoveEmployeeSkill, useSearchSkills, useSetEmployeeSkill } from "../lib/api";
import { ErrorBox } from "./ui";

const CATEGORIES = ["Technical", "Safety", "Quality", "Commercial", "Operations", "Managerial", "Digital", "Language"] as const;

type Props = { employee: string };

/** Per-employee Skill Catalogue rating panel (HR-01, HR-02): rate an existing catalogue skill, or add a new one to
 * the catalogue on the fly, then rate it — same "search or create" shape as the advance account picker. Shared
 * between the Employee list and Administration. */
export function EmployeeSkills({ employee }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("");
  const [rating, setRating] = useState(0);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState<string>(CATEGORIES[0]);

  const list = useEmployeeSkills(employee, open);
  const search = useSearchSkills(q, undefined, adding && !creating);
  const setSkill = useSetEmployeeSkill();
  const removeSkill = useRemoveEmployeeSkill();
  const createSkill = useCreateSkill();

  const rows = list.data ?? [];
  const resetAdd = () => { setAdding(false); setCreating(false); setQ(""); setPicked(""); setRating(0); setNewName(""); setNewCategory(CATEGORIES[0]); };

  const confirmAdd = () => {
    if (!picked || !rating) return;
    setSkill.mutate({ employee, skill: picked, proficiency: rating / 5 }, { onSuccess: resetAdd });
  };
  const confirmCreate = () => {
    if (!newName.trim() || !rating) return;
    createSkill.mutate({ skill_name: newName.trim(), category: newCategory }, {
      onSuccess: (skill) => setSkill.mutate({ employee, skill: skill.name, proficiency: rating / 5 }, { onSuccess: resetAdd }),
    });
  };

  const Stars = ({ value, onPick }: { value: number; onPick: (n: number) => void }) => (
    <div className="flex items-center gap-1 text-xs">
      <span>{t("ppl.skills.proficiency")}</span>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onPick(n)} className={`h-5 w-5 rounded border ${value >= n ? "border-brand-primary bg-blue-50 text-brand-primary" : "border-brand-grey-light"}`}>{n}</button>
      ))}
    </div>
  );

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-left text-xs hover:underline" data-skills-open={employee}>
        {rows.length > 0 ? t("ppl.skills.count", { n: rows.length }) : t("ppl.skills.button")}
      </button>
    );
  }

  return (
    <div className="w-72 rounded border border-brand-grey-light bg-white p-2 shadow-sm" data-skills-panel={employee}>
      <div className="text-[10px] uppercase tracking-wide text-brand-grey">{t("ppl.skills.panelTitle")}</div>
      {list.isLoading ? (
        <div className="py-1 text-xs text-brand-grey">{t("common.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="py-1 text-xs text-brand-grey">{t("ppl.skills.none")}</div>
      ) : (
        <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto">
          {rows.map((r) => (
            <li key={r.skill} className="flex items-center justify-between gap-2 text-xs" data-skill-row={r.skill}>
              <div className="min-w-0 truncate">
                <span className="font-medium">{r.skill}</span>
                {r.category && <span className="ml-1 text-[10px] text-brand-grey">({t(`ppl.skills.categories.${r.category}`, r.category)})</span>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="text-amber-500">{"★".repeat(Math.round((r.proficiency || 0) * 5))}</span>
                <button onClick={() => removeSkill.mutate({ employee, skill: r.skill })} className="text-brand-grey hover:text-status-red" data-skill-remove={r.skill}>
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="my-1 border-t border-brand-grey-light" />
      {!adding ? (
        <button onClick={() => setAdding(true)} className="rounded border border-brand-grey-light px-2 py-0.5 text-xs font-semibold text-brand-charcoal hover:bg-[#F6F8FA]" data-skill-add-open>
          {t("ppl.skills.add")}
        </button>
      ) : creating ? (
        <div className="space-y-1">
          <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("ppl.skills.newSkillName")}
            className="w-full rounded border border-brand-grey-light px-2 py-1 text-xs" data-skill-new-name />
          <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} className="w-full rounded border border-brand-grey-light px-1 py-1 text-xs" data-skill-new-category>
            {CATEGORIES.map((c) => <option key={c} value={c}>{t(`ppl.skills.categories.${c}`)}</option>)}
          </select>
          <Stars value={rating} onPick={setRating} />
          <div className="flex justify-end gap-1">
            <button onClick={resetAdd} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
            <button disabled={!newName.trim() || !rating || createSkill.isPending || setSkill.isPending} onClick={confirmCreate}
              className="rounded border border-brand-primary px-2 py-0.5 text-xs font-semibold text-brand-primary hover:bg-blue-50 disabled:opacity-50" data-skill-create-confirm>
              {t("ppl.skills.createSkill")}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ppl.skills.searchPlaceholder")}
            className="w-full rounded border border-brand-grey-light px-2 py-1 text-xs" data-skill-search />
          <select onChange={(e) => setPicked(e.target.value)} value={picked} className="w-full rounded border border-brand-grey-light px-1 py-1 text-xs" data-skill-select>
            <option value="" disabled>{search.isLoading ? t("common.loading") : t("ppl.skills.choose")}</option>
            {(search.data ?? []).map((s) => <option key={s.name} value={s.name}>{s.skill_name}</option>)}
          </select>
          <Stars value={rating} onPick={setRating} />
          <div className="flex items-center justify-between">
            <button onClick={() => setCreating(true)} className="text-xs text-brand-grey hover:text-brand-charcoal" data-skill-create-open>{t("ppl.skills.createNew")}</button>
            <div className="flex gap-1">
              <button onClick={resetAdd} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.cancel")}</button>
              <button disabled={!picked || !rating || setSkill.isPending} onClick={confirmAdd}
                className="rounded border border-brand-primary px-2 py-0.5 text-xs font-semibold text-brand-primary hover:bg-blue-50 disabled:opacity-50" data-skill-add-confirm>
                {t("ppl.skills.add")}
              </button>
            </div>
          </div>
        </div>
      )}
      {(setSkill.isError || removeSkill.isError || createSkill.isError) && (
        <div className="mt-1"><ErrorBox error={setSkill.error ?? removeSkill.error ?? createSkill.error} /></div>
      )}
      <div className="mt-1 text-right">
        <button onClick={() => setOpen(false)} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.close")}</button>
      </div>
    </div>
  );
}
